import React, {Component} from 'react';
import {
  Grid,
  Paper,
  Table, TableBody,
  TableCell,
  TableHead,
  TableRow,
  DialogContent,
  Dialog, CircularProgress, Typography, Button,
  DialogActions,
  styled,
  Box
} from "@mui/material";
import {useDropzone} from "react-dropzone";
import ErrorIcon from "@mui/icons-material/Cancel";
import {Progress} from "./Progress";
import {DataSet} from "./model";
import axios from "axios";
const filesize = require('filesize');

export interface DataSetPageProps {
}

export interface DataSetPageState {
  datasets: DataSet[];
  loading: boolean;
  failed: boolean;
  uploadProgressDialogOpen: boolean;
  uploadingDataset: boolean;
  uploadFailed: boolean;
  uploadErrorMessage: string | undefined;
}

const StyledTable = styled(Table)(() => ({}));

const StyledTableDrag = styled(Table)(() => ({
  opacity: 0.3
}));

const StyledDropZone = styled(Box)(() => ({
  position: 'relative',
}));

const StyledDropZoneActive = styled(Box)(() => ({
  position: 'relative',
  zIndex: 9999,
}));

const StyledDropZoneReject = styled(Box)(() => ({
  position: 'relative',
}));

const StyledOverlay = styled(Grid)(() => ({
  width: '100%',
  height: '100%',
  background: '#dddddd',
  opacity: 0.7,
  position: 'absolute',
  top: 0,
  left: 0
}));

const StyledProgress = styled(CircularProgress)(({ theme }) => ({
  margin: theme.spacing(2),
}));

const StyledErrorIcon = styled(ErrorIcon)(() => ({
  color: '#FF0000',
  width: '60px',
  height: '60px',
  marginBottom: '10px'
}));

// Global style components
const StyledInnerContentRoot = styled(Box)(() => ({
  marginLeft: 'auto',
  marginRight: 'auto',
  marginTop: 10,
  maxWidth: 1200,
}));

const StyledInnerContentPaper = styled(Paper)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: '0px -1px 2px 0px rgba(0,0,0,0.1), 0px 2px 2px 0px rgba(0,0,0,0.1), 0px 1px 1px -2px rgba(0,0,0,0.1)',
}));

const StyledTableCell = styled(TableCell)(() => ({
  cursor: 'pointer',
  paddingLeft: '16px',
  paddingRight: '16px',
  fontSize: '0.9375em'
}));

const StyledTableCellBold = styled(TableCell)(() => ({
  cursor: 'pointer',
  paddingLeft: '16px',
  paddingRight: '16px',
  fontSize: '0.9375em',
  fontWeight: 500
}));

const StyledTableCellPlaceholder = styled(TableCell)(() => ({
  textAlign: 'center',
  fontSize: '0.9375em'
}));

const StyledHeaderLeft = styled(Grid)(({ theme }) => ({
  marginLeft: '24px',
  marginTop: '10px',
}));

const StyledHeaderRight = styled(Grid)(({ theme }) => ({
  marginRight: '24px',
  marginTop: '10px',
}));

const StyledHeaderLabel = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
  fontSize: '1.05em'
}));

const StyledHeader = styled(Grid)(({ theme }) => ({
  marginTop: '24px'
}));

const StyledError = styled(Box)(({ theme }) => ({
  color: theme.palette.error.contrastText,
  marginTop: 1,
  marginBottom: 10,
  background: theme.palette.error.main,
  fontSize: '0.9375em',
  padding: 5,
  height: 70,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center'
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  margin: 0,
  padding: theme.spacing(1),
}));

class DataSetPage extends Component<DataSetPageProps, DataSetPageState> {

  state = {
    datasets: [],
    loading: true,
    failed: false,
    uploadProgressDialogOpen: false,
    uploadingDataset: false,
    uploadFailed: false,
    uploadErrorMessage: undefined
  };


  componentDidMount(): void {
    this.fetchDatasets()
  }

  formatAsPercentage(value: number, total: number): string {
    if (value && total && total > 0) {
      return `${((value) / total * 100).toLocaleString('de-AT', {
        minimumIntegerDigits: 1,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}%`;
    }
    return "-";
  }

  async fetchDatasets() {
    try {
      let response = await axios.get('/api/admin/datasets');
      this.setState({loading: false, failed: false, datasets: response.data.datasets});
    } catch(error) {
      this.setState({loading: false, failed: true, datasets: []});
    }
  }

  async onDrop(acceptedFiles: any[]) {
    if (acceptedFiles.length !== 1) {
      this.setState({uploadProgressDialogOpen: true, uploadFailed: true, uploadErrorMessage: "Sie müssen 1 SQLite-Datenbank hochladen"});
      return;
    }
    let filename: string = acceptedFiles[0].name;
    if (filename.toLowerCase().endsWith('sqlite') === false && filename.toLowerCase().endsWith('sqlite2') === false) {
      this.setState({uploadProgressDialogOpen: true, uploadFailed: true, uploadErrorMessage: "Die verwendete Datei ist scheinbar keine SQLite-Datenbank"});
      return;
    }

    this.setState({uploadProgressDialogOpen: true, uploadFailed: false, uploadErrorMessage: undefined, uploadingDataset: true});
    try {
      const formData = new FormData();
      formData.append('file', acceptedFiles[0]);
      const config = {
        headers: {
          'content-type': 'multipart/form-data'
        }
      };

      await axios.post(`/api/admin/datasets`, formData, config);

      await this.fetchDatasets();

      this.setState({uploadProgressDialogOpen: false, uploadFailed: false, uploadErrorMessage: undefined, uploadingDataset: false});
    } catch (error) {
      this.setState({uploadProgressDialogOpen: true, uploadFailed: true, uploadErrorMessage: undefined, uploadingDataset: false});
    }
  }

  renderTable(dragActive: boolean) {
    let datasets : DataSet[] = this.state.datasets;
    const TableComponent = dragActive ? StyledTableDrag : StyledTable;

    return (
      <>
        <TableComponent>
          <TableHead>
            <TableRow>
              <StyledTableCell padding={"none"}>Datum</StyledTableCell>
              <StyledTableCell padding={"none"}>Upload von</StyledTableCell>
              <StyledTableCell padding={"none"}>Größe (original/gezipped)</StyledTableCell>
              <StyledTableCell padding={"none"}>Verteilung %</StyledTableCell>
              <TableCell style={{width: '5px'}}/>
            </TableRow>
          </TableHead>
          <TableBody>
            {datasets.length === 0 &&
            <TableRow key={"placeholder"}>
              <StyledTableCellPlaceholder colSpan={4}>
                Noch keine Datensätze erfasst
              </StyledTableCellPlaceholder>
            </TableRow>
            }
            {datasets.map(row => {
              const CellComponent = row.is_current_dataset ? StyledTableCellBold : StyledTableCell;
              return (
                <TableRow key={row.identifier}>
                  <CellComponent component="th" scope="row">
                    {row.visible_identifier} { row.is_current_dataset ? ' (Aktiv)' : ''}
                  </CellComponent>
                  <CellComponent>
                    {row.uploader_name}
                  </CellComponent>
                  <CellComponent>
                    {filesize(row.size_original)} / {filesize(row.size_zipped)}
                  </CellComponent>
                  <CellComponent>
                  {this.formatAsPercentage(row.active_device_count, row.total_device_count)}
                  </CellComponent>
                  <TableCell/>
                </TableRow>
              );
            })}
          </TableBody>
        </TableComponent>
      </>
    );
  }

  render() {
    if (this.state.failed) {
      return (
        <StyledInnerContentRoot>
          <StyledError>Fehler beim Laden der Datensätze</StyledError>
        </StyledInnerContentRoot>
      );
    } else if (this.state.loading) {
      return (
        <Progress/>
      );
    }

    return (
      <StyledInnerContentRoot>
        <StyledHeader container direction="row" justifyContent="space-between" alignItems="flex-end">
          <StyledHeaderLeft item>
            <StyledHeaderLabel>
              Datensätze
            </StyledHeaderLabel>
          </StyledHeaderLeft>

          <StyledHeaderRight item>
            <StyledHeaderLabel>
              <DropzoneButton onDrop={(files) => this.onDrop(files)} />
            </StyledHeaderLabel>
          </StyledHeaderRight>

        </StyledHeader>
        <StyledInnerContentPaper>
          <DropzoneArea onDrop={(files) => this.onDrop(files)} renderTable={(isDragActive) => this.renderTable(isDragActive)} />
        </StyledInnerContentPaper>
        <Dialog
          open={this.state.uploadProgressDialogOpen}
          onClose={() => {}}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description">
          <DialogContent>
            {this.state.uploadingDataset &&
            <Grid container justifyContent={"center"}>
              <Grid item>
                <StyledProgress/>
              </Grid>
            </Grid>
            }
            {this.state.uploadFailed &&
            <Grid container justifyContent={"center"}>
              <Grid item>
                <StyledErrorIcon/>
              </Grid>
            </Grid>

            }
            { this.state.uploadingDataset === true &&
            <Typography>
              Datensatz wird hochgeladen...
            </Typography>
            }
            { this.state.uploadFailed === true &&
            <Typography>
              { this.state.uploadErrorMessage ? this.state.uploadErrorMessage : "Datensatz konnte nicht hochgeladen werden!" }
            </Typography>
            }
          </DialogContent>
          {this.state.uploadFailed === true &&
          <StyledDialogActions>
            <Button onClick={() => {this.setState({uploadProgressDialogOpen: false, uploadFailed: false })}} color="primary">
              OK
            </Button>
          </StyledDialogActions>
          }
        </Dialog>
      </StyledInnerContentRoot>
    )
  }
}

// Functional component for the dropzone button
interface DropzoneButtonProps {
  onDrop: (files: File[]) => void;
}

const DropzoneButton: React.FC<DropzoneButtonProps> = ({ onDrop }) => {
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    multiple: false,
    noClick: true,
    noKeyboard: true
  });

  return (
    <>
      <div {...getRootProps()}>
        <input {...getInputProps()} />
      </div>
      <Button color="primary" variant="contained" onClick={open}>
        Datensatz hochladen
      </Button>
    </>
  );
};

// Functional component for the dropzone area
interface DropzoneAreaProps {
  onDrop: (files: File[]) => void;
  renderTable: (isDragActive: boolean) => React.ReactNode;
}

const DropzoneArea: React.FC<DropzoneAreaProps> = ({ onDrop, renderTable }) => {
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    multiple: false,
    noClick: true
  });

  const DropZoneComponent = isDragReject ? StyledDropZoneReject : (isDragActive ? StyledDropZoneActive : StyledDropZone);

  return (
    <DropZoneComponent {...getRootProps()}>
      <input {...getInputProps()} />
      {renderTable(isDragActive)}

      {isDragAccept && (
        <StyledOverlay container justifyContent="center" alignItems="center">
          <Grid item>
            Loslassen, um Datensatz hochzuladen
          </Grid>
        </StyledOverlay>
      )}
    </DropZoneComponent>
  );
};

export default DataSetPage;